import React from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Avatar,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';

interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

interface FilterUserAutocompleteProps {
  users: User[];
  value?: string | null;
  onChange: (userId: string | undefined) => void;
  label?: string;
  size?: 'small' | 'medium';
  sx?: any;
}

const getUserInitials = (name: string): string => {
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const getAvatarColor = (name: string): string => {
  const colors = [
    '#1976d2', '#dc004e', '#9c27b0', '#673ab7',
    '#3f51b5', '#2196f3', '#00bcd4', '#009688',
    '#4caf50', '#8bc34a', '#ff9800', '#ff5722',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export const FilterUserAutocomplete: React.FC<FilterUserAutocompleteProps> = ({
  users,
  value,
  onChange,
  label = 'Assignee',
  size = 'small',
  sx = {},
}) => {
  // Sort users alphabetically by name
  const sortedUsers = [...users].sort((a, b) => {
    const nameA = a.name || a.email || '';
    const nameB = b.name || b.email || '';
    return nameA.localeCompare(nameB);
  });

  // Find the selected user
  const selectedUser = sortedUsers.find(u => u.id === value) || null;

  const handleChange = (_event: any, newValue: User | null) => {
    onChange(newValue?.id || undefined);
  };

  return (
    <Autocomplete
      value={selectedUser}
      onChange={handleChange}
      options={sortedUsers}
      size={size}
      getOptionLabel={(option) => option.name || option.email}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <ListItemAvatar>
            <Avatar
              src={option.avatar}
              sx={{
                width: 28,
                height: 28,
                bgcolor: !option.avatar ? getAvatarColor(option.name || option.email) : undefined,
                fontSize: '0.75rem',
              }}
            >
              {!option.avatar && (option.name ? getUserInitials(option.name) : <PersonIcon sx={{ fontSize: 14 }} />)}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={option.name || option.email}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          />
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size={size}
          InputProps={{
            ...params.InputProps,
            startAdornment: selectedUser && (
              <Avatar
                src={selectedUser.avatar}
                sx={{
                  width: 20,
                  height: 20,
                  mr: 0.5,
                  bgcolor: !selectedUser.avatar ? getAvatarColor(selectedUser.name || selectedUser.email) : undefined,
                  fontSize: '0.65rem',
                }}
              >
                {!selectedUser.avatar && (selectedUser.name ? getUserInitials(selectedUser.name) : <PersonIcon sx={{ fontSize: 12 }} />)}
              </Avatar>
            ),
          }}
        />
      )}
      sx={sx}
    />
  );
};